import * as Wildfire from "./layers/static/Wildfire";

import {
  camerasAZGF,
  camerasCoconinoMuseum,
  camerasGilaTelegraph,
  camerasNavajo,
  camerasPima,
  camerasPinal,
  camerasSantaCruz,
  pipelineFire,
} from "./layers/misc/cameras";

import { IQueryFeaturesOptions } from "@esri/arcgis-rest-feature-service";
import { Basemaps, DynamicMapLayerOptions } from "esri-leaflet";
import { LatLng } from "leaflet";
import { ReactNode } from "react";
import { Lang } from "./contexts/LocaleContext";
import FEMA_NFHL from "./layers/dynamic/FEMA_NFHL";
import { IPanel } from "./models/Menus";
import {
  ISensorDisplayPrecip,
  ISensorDisplayStream,
} from "./models/SensorDisplay";

export interface StaticMapLayer {
  name: string;
  source: string | IQueryFeaturesOptions;
  color?: string;
  visible?: boolean;
}

export interface StaticMapLayerSubFolder {
  title: string;
  layers: StaticMapLayer[];
}

export interface StaticMapLayerFolder {
  title: string;
  layers: (StaticMapLayer | StaticMapLayerSubFolder)[];
  cite_text?: string;
  cite_url?: string;
}

export type StaticMapLayerOrFolder = StaticMapLayer | StaticMapLayerFolder;
export interface DynamicMapLayer extends DynamicMapLayerOptions {
  title: string;
  defaultLayers: number[];
}

export interface CameraLayerCamera {
  name: string;
  url: string;
  location: LatLng;
}

export interface MiscCameraLayer {
  name: string;
  cameras: CameraLayerCamera[];
  visible?: boolean;
}

export interface MiscLayerFolder {
  title: string;
  layers: MiscCameraLayer[];
}

export type MiscLayerOrFolder = MiscCameraLayer | MiscLayerFolder;

export interface Config {
  title: string;
  center: LatLng;
  zoom: number;
  basemap: Basemaps;
  disclaimer: { body: ReactNode; outLink: string } | false;
  agencyPassList?: string[];
  defaultDisplay: ISensorDisplayPrecip | ISensorDisplayStream;
  defaultLocale: Lang;
  defaultMenuPanel?: IPanel;
  layers: {
    static: StaticMapLayerOrFolder[];
    dynamic: DynamicMapLayer[];
    misc: MiscLayerOrFolder[];
    lightning: boolean;
    cbrfc: boolean;
    nwsWarnings: boolean;
  };
  precipIntervals: {
    default: string;
    base: string[];
    more: string[];
  };
  streamPeriods: {
    default: string;
  };
  markers: {
    precip: {
      dotDisplayZoomLevel: number | false;
      dot: {
        breakValues: number[];
        colors: string[];
      };
      tag: {
        breakValues: number[];
        textColors: string[];
        backgroundColors: string[];
      };
    };
    stream: {
      stageBreakValues: number[];
      dischargeBreakValues: number[];
      textColors: string[];
      backgroundColors: string[];
    };
    missing: string | false;
    camera: string | false;
  };
  hideRecentDataLink?: boolean;
  radar: boolean;
  radarLegacy?: boolean;
}

const config: Config = {
  title: "JE Fuller ALERT",
  center: new LatLng(34.177027, -111.714981),
  zoom: 8,
  basemap: "Topographic",
  disclaimer: false,
  defaultDisplay: { type: "precip", interval: "P1D", measure: "total" },
  defaultMenuPanel: "sensors",
  defaultLocale: "en",
  layers: {
    static: [
      Wildfire.last(4),
      {
        source: process.env.PUBLIC_URL + "/kml/Arizona_Counties.kml",
        name: "County Boundary",
      },
    ],
    dynamic: [FEMA_NFHL],
    misc: [
      {
        name: "AZ Game & Fish Dept. Cameras",
        cameras: camerasAZGF,
      },
      { name: "Navajo County", cameras: camerasNavajo },
      { name: "Pima County Cameras", cameras: camerasPima },
      { name: "Pinal County Cameras", cameras: camerasPinal },
      { name: "Santa Cruz County Cameras", cameras: camerasSantaCruz },
      { name: "Telegraph Fire Cameras", cameras: camerasGilaTelegraph },
      { name: "Museum Fire Cameras", cameras: camerasCoconinoMuseum },
      { name: "Pipeline Fire Cameras", cameras: pipelineFire },
    ],
    lightning: false,
    cbrfc: false,
    nwsWarnings: false,
  },
  precipIntervals: {
    default: "P1D",
    base: ["PT1H", "PT12H", "P1D"],
    more: ["PT15M", "PT30M", "PT3H", "PT6H", "P7D", "P30D"],
  },
  streamPeriods: {
    default: "last",
  },
  markers: {
    precip: {
      dotDisplayZoomLevel: 9,
      dot: {
        breakValues: [0.01, 0.25, 0.5, 1, 2],
        colors: ["#6ECFCF", "#3BB9FF", "#52D017", "yellow", "orange", "red"],
      },
      tag: {
        breakValues: [0.01, 0.25, 0.5, 1, 2],
        backgroundColors: [
          "#6ECFCF",
          "#3BB9FF",
          "#52D017",
          "yellow",
          "orange",
          "red",
        ],
        textColors: [
          "black",
          "black",
          "black",
          "black",
          "black",
          "white",
          "black",
        ],
      },
    },
    stream: {
      stageBreakValues: [1, 5, 10, 20],
      dischargeBreakValues: [10, 20, 250, 1000],
      backgroundColors: ["#3BB9FF", "#52D017", "yellow", "orange", "red"],
      textColors: ["black", "black", "black", "black", "white"],
    },
    missing: false,
    camera: false,
  },
  radar: true,
};

export default config;
